/**
 * SOLID Review
 * S — Strong. One concern: the 3D cups' and dice's geometry, and the upper cup's poses derived from it.
 * O — Acceptable. A new proportion is a change here that the poses follow.
 * L — N/A — data.
 * I — Strong. Named numbers.
 * D — Strong. Imports nothing.
 */

// THE CUPS' GEOMETRY (frontend-framework-oo P4, 2026-10-29, §28.296) — `GameScene.tsx`'s constants until P4: its view draws the cups
// and dice with them and the cup's motion (`viewmodels/CupMotion.ts`) poses the upper cup with them.

/** A die's edge. */
export const DIE_SIZE   = 0.26;

// ---------------------------------------------------------------------------
// Constants — two identical cups, both turned UPSIDE DOWN
//
//   Lower cup : INVERTED — wide mouth (R_WIDE) open on table,
//               narrow closed base (R_NARROW) UP at highest elevation.
//               Dice rest on that small-diameter elevated base.
//   Upper cup : INVERTED — wide mouth (R_WIDE) DOWN over the dice,
//               narrow closed base (R_NARROW) at top. Lifts/tilts to reveal.
// ---------------------------------------------------------------------------

// Proportions: Height 9 : top diameter 7.5 : bottom diameter 6 (≈ 1.2 : 1 : 0.8),
// anchored on the existing top radius (R_WIDE) so the cup's footprint is unchanged.
export const R_WIDE   = 0.52;                          // open mouth (wide end) — anchor, unchanged
export const CUP_H    = R_WIDE * 2 * (9 / 7.5);        // ≈ 1.248
export const R_NARROW = R_WIDE * (6 / 7.5);            // closed base (narrow end) — the dice platform, ≈ 0.416

// CylinderGeometry centers at group origin.
export const LOWER_Y    = CUP_H / 2;          // ≈ 0.624 — wide mouth at table y=0, narrow base up at y=CUP_H

// Both cups share the same taper, so sliding the upper cup down by `d` shifts its radius
// profile down by `d` too — at every height in the overlap zone the upper cup's radius
// exceeds the lower cup's by a constant gap of d * (R_WIDE - R_NARROW) / CUP_H.
// Covering 6 of the 9 proportional height-units (d = CUP_H * 6/9) makes that gap equal
// exactly 0.5 proportional units (6 * 0.75/9), matching a 0.5-unit cup wall thickness —
// i.e. the upper cup nests snugly over the lower cup with no gap or clipping.
const CUP_OVERLAP = CUP_H * (6 / 9);   // ≈ 0.832 — height of the lower cup covered by the upper cup

const PLATFORM_Y = CUP_H;              // ≈ 1.248 — narrow elevated base (dice platform)
const UPPER_Y    = CUP_H + CUP_H / 2 - CUP_OVERLAP;  // ≈ 1.040 — upper cup center, lowered to nest over the lower cup

// Dice rest ON the narrow elevated base
export const DICE_Y  = PLATFORM_Y + DIE_SIZE / 2 + 0.005; // ≈ 1.383

// ---------------------------------------------------------------------------
// Peek pose — the upper cup tips up about its BACK-bottom rim (like lifting a
// bucket by its front edge), NOT by raising the whole cup straight up.
//
// Why pivot about the back rim: the dice sit at the scene center (z≈0). If the cup
// is lifted+tilted about its own centre, a tilt large enough to open a visible front
// gap also swings the *back* rim up and forward, past the dice — so players "behind"
// the scene can see the dice. Pivoting about the back rim instead keeps that rim
// planted behind+below the dice, so the cup's tall back wall always blocks the rear
// sightline, while only the front swings up to reveal the dice to the active roller.
//
// At rest the pivot sits at the upper cup's back-bottom (wide mouth) rim, nested over
// the lower cup:
export const PIVOT_REST_Y = UPPER_Y - CUP_H / 2;   // ≈ 0.416 — mouth/back-rim height at rest
export const PIVOT_REST_Z = -R_WIDE;               // ≈ -0.52 — back rim, behind the dice (z≈0)

// Peek target (per spec): the cup tips up about its back-bottom rim so that rim — the
// upper cup's *visual bottom*, its lowest point — comes down to meet the *visual top*
// of the lower cup (its narrow base, the platform at PLATFORM_Y). Centred in x, and
// pulled to z = -0.6 (inward, away from the viewer).
export const PEEK_PIVOT_Y = PLATFORM_Y;         // ≈ 1.248 — visual top of the lower cup
export const PEEK_PIVOT_Z = -0.6;               // inward, away from the viewer
export const PEEK_TILT    = -0.5 - (7 * Math.PI) / 180; // rad — mouth tips up toward camera, +7° further back
export const REVEAL_LIFT  = CUP_H * 2.3;        // full "Lift Cup" reveal — cup rises clear of the scene
